import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * The StarScribe list in the main area (picture 34, the owner's pick A: the "StarScribe" item in the menu opens it):
 *  - the title and "+ New page";
 *  - "Search your pages by title" (the route's title search);
 *  - the tabs My pages, Shared with me and Recently deleted.
 * A row opens its page beside the chat. In Recently deleted (picture 54) each page says when it was deleted and when it is
 * erased, with Restore and Erase now, above the line about the 30 days a page is kept.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { NotebookPen, Plus, Search, Trash2 } from '../starscribeIcons.js';
import { pagePath, parsePageRow, parsePageRows, refusalText } from './pages.js';
import { deletedLine, pageAge } from './words.js';
const TABS = [
    { kind: 'mine', label: 'My pages' },
    { kind: 'shared', label: 'Shared with me' },
    { kind: 'deleted', label: 'Recently deleted' },
];
/** The search asks again this long after the last key. */
export const SEARCH_DELAY_MS = 250;
/** How long a deleted page is kept (the service's sweep), for a row that does not say. */
const KEPT_MS = 30 * 24 * 60 * 60 * 1000;
/** The line under Recently deleted (picture 54). */
export const DELETED_NOTE = 'A deleted page stays here for 30 days and can be restored until then. After that it is erased.';
function firstName(name) {
    return name.trim().split(/\s+/)[0] ?? name;
}
/** "Dana", "Dana and Marcus", "Dana and 2 others". */
function names(people) {
    const first = people.map(firstName);
    if (first.length <= 1)
        return first[0] ?? '';
    if (first.length === 2)
        return `${first[0]} and ${first[1]}`;
    return `${first[0]} and ${first.length - 1} others`;
}
function rowLine(row, kind, nowMs) {
    const edited = `Edited ${pageAge(row.updatedAtMs, nowMs)}`;
    if (kind === 'shared')
        return row.ownerName ? `${edited} · from ${firstName(row.ownerName)}` : edited;
    if (row.sharedWith?.length)
        return `${edited} · shared with ${names(row.sharedWith)}`;
    return row.shared ? `${edited} · shared` : edited;
}
/** The faces of a shared page of the person's (picture 34): theirs first, in Gyro's red, then up to two others. */
function Faces({ you, others }) {
    const colours = ['#1f9d8a', '#3485dc'];
    const people = [you || 'You', ...others.slice(0, 2)];
    return _jsx("span", { className: "gyro-ss-faces", "aria-hidden": "true", children: people.map((person, index) => _jsx("span", { className: "gyro-ss-face", style: { background: index === 0 ? '#d13238' : colours[(index - 1) % colours.length] }, children: (person.trim()[0] ?? '?').toUpperCase() }, `${index}:${person}`)) });
}
export function StarScribeList({ request, you, onOpen, nowMs = Date.now }) {
    const [kind, setKind] = useState('mine');
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [listing, setListing] = useState({ state: 'loading', kind: 'mine' });
    const [revision, setRevision] = useState(0);
    const [working, setWorking] = useState(null);
    const [problem, setProblem] = useState('');
    const [said, setSaid] = useState('');
    const headingId = useId();
    const tabId = useId();
    const tabs = useRef(new Map());
    const liveRef = useRef(true);
    useEffect(() => { liveRef.current = true; return () => { liveRef.current = false; }; }, []);
    // The search goes to the service a moment after the last key.
    useEffect(() => {
        const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [query]);
    // The tab's pages: a new tab starts from "Loading"; a new search keeps the rows on screen until its answer.
    useEffect(() => {
        const controller = new AbortController();
        setListing((current) => current.state === 'ready' && current.kind === kind ? current : { state: 'loading', kind });
        request('/v1/starscribe/pages', { query: { which: kind, ...(search ? { q: search } : {}) }, signal: controller.signal }).then((raw) => { if (!controller.signal.aborted)
            setListing({ state: 'ready', kind, rows: parsePageRows(raw) }); }, (failure) => { if (!controller.signal.aborted)
            setListing({ state: 'error', kind, message: refusalText(failure, 'StarScribe could not list your pages. Try again.') }); });
        return () => controller.abort();
    }, [request, kind, search, revision]);
    const dropRow = (pageId) => setListing((current) => current.state === 'ready' ? { ...current, rows: current.rows.filter((row) => row.pageId !== pageId) } : current);
    /** One change at a time from the list; a refusal is said above the rows. */
    const act = async (id, work, fallback, done) => {
        if (working)
            return;
        setWorking(id);
        setProblem('');
        setSaid('');
        try {
            await work();
            if (liveRef.current)
                setSaid(done);
        }
        catch (failure) {
            if (liveRef.current)
                setProblem(refusalText(failure, fallback));
        }
        finally {
            if (liveRef.current)
                setWorking(null);
        }
    };
    const newPage = () => void act('new', async () => {
        const answered = await request('/v1/starscribe/pages', { method: 'POST', body: {} });
        const page = parsePageRow(answered?.page);
        if (!page)
            throw new Error('no page in the answer');
        onOpen({ pageId: page.pageId, title: page.title });
    }, 'StarScribe could not make a new page. Try again.', '');
    const restore = (row) => void act(row.pageId, async () => {
        await request(pagePath(row.pageId, 'recover'), { method: 'POST' });
        dropRow(row.pageId);
    }, `StarScribe could not restore ${row.title}. Try again.`, `${row.title} is back in My pages.`);
    const erase = (row) => void act(row.pageId, async () => {
        await request(pagePath(row.pageId, 'purge'), { method: 'DELETE' });
        dropRow(row.pageId);
    }, `StarScribe could not erase ${row.title}. Try again.`, `${row.title} is erased.`);
    const choose = (next, focus = false) => {
        setKind(next);
        setProblem('');
        if (focus)
            requestAnimationFrame(() => tabs.current.get(next)?.focus());
    };
    const tabKey = (event) => {
        const index = TABS.findIndex((tab) => tab.kind === kind);
        const next = event.key === 'ArrowRight' ? TABS[(index + 1) % TABS.length]
            : event.key === 'ArrowLeft' ? TABS[(index + TABS.length - 1) % TABS.length]
                : event.key === 'Home' ? TABS[0]
                    : event.key === 'End' ? TABS[TABS.length - 1] : undefined;
        if (!next)
            return;
        event.preventDefault();
        choose(next.kind, true);
    };
    const now = nowMs();
    const current = listing.kind === kind ? listing : { state: 'loading', kind };
    const rows = current.state === 'ready' ? current.rows : [];
    const empty = search ? `No pages match "${search}".`
        : kind === 'mine' ? 'You have no pages yet. Make one with New page, or with Edit in StarScribe under an answer.'
            : kind === 'shared' ? 'No one has shared a page with you yet.'
                : 'Nothing was deleted in the last 30 days.';
    return _jsx("section", { className: "gyro-body gyro-ss-list", "aria-labelledby": headingId, children: _jsxs("div", { className: "gyro-ss-list-inner", children: [_jsxs("div", { className: "gyro-ss-list-head", children: [_jsx("h1", { id: headingId, className: "gyro-ss-list-title", children: "StarScribe" }), _jsxs("button", { type: "button", className: "gyro-ss-pill gyro-ss-new", onClick: newPage, disabled: working !== null, children: [_jsx(Plus, { size: 14, "aria-hidden": "true" }), _jsx("span", { children: "New page" })] })] }), _jsxs("label", { className: "gyro-ss-search", children: [_jsx(Search, { size: 15, "aria-hidden": "true" }), _jsx("input", { type: "search", value: query, onChange: (event) => setQuery(event.target.value), placeholder: "Search your pages by title", "aria-label": "Search your pages by title" })] }), _jsx("div", { className: "gyro-ss-tabs", role: "tablist", "aria-label": "Pages", onKeyDown: tabKey, children: TABS.map((tab) => _jsx("button", { ref: (button) => { if (button)
                            tabs.current.set(tab.kind, button);
                        else
                            tabs.current.delete(tab.kind); }, type: "button", role: "tab", id: `${tabId}-${tab.kind}`, className: "gyro-ss-tab", "aria-selected": tab.kind === kind, "aria-controls": `${tabId}-panel`, tabIndex: tab.kind === kind ? 0 : -1, onClick: () => choose(tab.kind), children: tab.label }, tab.kind)) }), problem && _jsx("p", { className: "gyro-ss-problem", role: "alert", children: problem }), _jsxs("div", { className: "gyro-ss-rows", role: "tabpanel", id: `${tabId}-panel`, "aria-labelledby": `${tabId}-${kind}`, "data-kind": kind, "aria-busy": current.state === 'loading' || undefined, children: [current.state === 'loading' && _jsx("p", { className: "gyro-ss-status", role: "status", children: "Loading your pages\u2026" }), current.state === 'error' && _jsxs("div", { className: "gyro-ss-status", role: "alert", children: [_jsx("span", { children: current.message }), _jsx("button", { type: "button", className: "gyro-ss-pill", onClick: () => setRevision((value) => value + 1), children: "Try again" })] }), current.state === 'ready' && rows.length === 0 && _jsx("p", { className: "gyro-ss-status", children: empty }), rows.length > 0 && _jsx("ul", { className: "gyro-ss-row-list", children: kind !== 'deleted' ? rows.map((row) => _jsx("li", { children: _jsxs("button", { type: "button", className: "gyro-ss-row", onClick: () => onOpen({ pageId: row.pageId, title: row.title }), children: [_jsx(NotebookPen, { size: 17, className: "gyro-ss-row-icon", "aria-hidden": "true" }), _jsxs("span", { className: "gyro-ss-row-text", children: [_jsx("span", { className: "gyro-ss-row-title", children: row.title }), _jsx("span", { className: "gyro-ss-row-meta", children: rowLine(row, kind, now) })] }), kind === 'mine' && row.sharedWith?.length ? _jsx(Faces, { you: you.name, others: row.sharedWith }) : null] }) }, row.pageId)) : rows.map((row) => _jsxs("li", { className: "gyro-ss-row gyro-ss-row-deleted", children: [_jsx(Trash2, { size: 17, className: "gyro-ss-row-icon", "aria-hidden": "true" }), _jsxs("span", { className: "gyro-ss-row-text", children: [_jsx("span", { className: "gyro-ss-row-title", children: row.title }), _jsx("span", { className: "gyro-ss-row-meta", children: deletedLine(row.deletedAtMs ?? row.updatedAtMs, row.purgeAtMs ?? (row.deletedAtMs ?? row.updatedAtMs) + KEPT_MS, now) })] }), _jsx("button", { type: "button", className: "gyro-ss-pill", onClick: () => restore(row), disabled: working !== null, "aria-label": `Restore ${row.title}`, children: "Restore" }), _jsx("button", { type: "button", className: "gyro-ss-danger", onClick: () => erase(row), disabled: working !== null, "aria-label": `Erase now: ${row.title}`, children: "Erase now" })] }, row.pageId)) }), kind === 'deleted' && current.state !== 'error' && _jsx("p", { className: "gyro-ss-note", children: DELETED_NOTE })] }), _jsx("span", { className: "gyro-sr-only", role: "status", children: said })] }) });
}
//# sourceMappingURL=StarScribeList.js.map